// Introspection over the extended zod schemas: walk a declared object's fields, unwrap the modifiers that do
// not change what a field IS, collect merged author meta (outer wins), and derive the param type +
// `uiComponent` widget. Everything here reads a schema and returns plain data — it is the layer both the
// runner's model builder and the console's renderer sit on top of.
//
// This module DESCRIBES a schema; it never decides whether the schema is allowed. A declaration the runtime
// cannot render is refused at registration by ./assert-supported-schema.ts, which reads `resolvedType` from
// here. Keeping the two apart is what lets the runtime's own SYSTEM schemes — which model a wire contract
// rather than a rendered form — be described by the same code that refuses an author writing the same thing
// in a parameter.
import {  FlowExtensionParamWidgetTypes } from '../widget-types'
import type { FrMeta } from '../zod'

export interface Inspected {
  // The unwrapped core schema (past the modifiers below).
  core: AnyZod
  // Core zod def type: 'string' | 'number' | 'boolean' | 'enum' | 'date' | 'object' | 'array' | …
  // Defaulted to 'string' when the schema reports none, so every consumer has something to switch on.
  coreType: string
  // The SAME answer, undefined rather than defaulted. What the registration guard has to read: in `coreType`
  // "declared nothing" and "declared a string" are the same value, and a policy decision cannot conflate them.
  resolvedType?: string
  /**
   * A `z.union()`'s declared branches, outermost union only — `undefined` for everything else.
   *
   * `core` and `coreType` above have already been resolved to the FIRST branch, which is what publishes and
   * what picks the widget. That answer is right for every consumer that only needs a type, and wrong for the
   * two that must consider all of them: the registration guard has to refuse a union with an unsupported
   * branch, and coercion has to try each branch in turn. They read this.
   */
  branches?: AnyZod[]
  required: boolean
  defaultValue: unknown
  meta: FrMeta
}

// Structural zod shape — we read internals (`_zod.def`) rather than depend on zod's public generics.
export type AnyZod = {
  _zod?: { def?: Record<string, unknown> }
  meta?: () => Record<string, unknown> | undefined
  shape?: Record<string, AnyZod>
  options?: unknown[]
}

const OPTIONAL_WRAPPERS = new Set(['optional', 'nullable', 'nullish'])
export const DEFAULT_WRAPPERS = new Set(['default', 'prefault'])

/**
 * Modifiers that constrain or re-shape a value without changing what the AUTHOR declared it to be. The
 * runtime looks straight through them: they decide nothing about the widget, the published type, or which
 * coercion runs.
 *
 * - `readonly` — freezes zod's output; the declared type is the inner one.
 * - `pipe` — `.pipe(b)` and `.transform(fn)` both produce this. We follow `def.in`, the INPUT side, because
 *   the published model describes **what a caller supplies**: it is what the console renders a form for, what
 *   the AI-tool schema advertises, and what the coercion layer must convert to before zod sees the value.
 *   What `execute` ends up receiving is the pipe's own business and is not a published fact.
 * - `lazy` — a thunk, for a recursive declaration.
 *
 * `.catch()` is deliberately NOT here. It is refused at registration instead (see
 * ./assert-supported-schema.ts): unwrapping it would fix the published type while leaving the declaration
 * silently substituting a fallback for invalid input, which is the more serious half of that defect.
 */
export const TRANSPARENT_WRAPPERS = new Set(['readonly', 'pipe', 'lazy'])

// Where the schema inside a wrapper lives — it is not always `innerType`.
export function innerOf(def: Record<string, unknown>, type: string): AnyZod | undefined {
  if (type === 'pipe') {
    return def.in as AnyZod | undefined
  }

  if (type === 'lazy') {
    return (def.getter as (() => AnyZod) | undefined)?.()
  }

  return def.innerType as AnyZod | undefined
}

// A self-referential `z.lazy()` would peel forever. Nothing legitimate nests modifiers anywhere near this deep.
// Shared with ../json/encode.ts, whose own wrapper walk has to stop on the same declarations this one does.
export const MAX_UNWRAP_DEPTH = 64

export function inspect(schema: AnyZod): Inspected {
  let current: AnyZod | undefined = schema
  // An `.optional()` / `.default()` seen OUTSIDE any `.nonoptional()`. The chain is walked outer→inner and
  // the outermost modifier is the author's final word, so `.optional().nonoptional()` is required while
  // `.nonoptional().optional()` is not — `sealed` is what stops an inner one from speaking for an outer one.
  // Mirrors `declaredAbsence` in ./field-plan.ts, which has to reach the same answer.
  let optional = false
  let sealed = false
  let defaultValue: unknown
  let branches: AnyZod[] | undefined
  let depth = 0
  const metas: Record<string, unknown>[] = []

  while (current?._zod && depth++ < MAX_UNWRAP_DEPTH) {
    const meta = typeof current.meta === 'function' ? current.meta() : undefined

    if (meta) {
      metas.push(meta)
    }

    const def = current._zod.def || {}
    const type = def.type as string

    if (OPTIONAL_WRAPPERS.has(type)) {
      optional ||= !sealed
      current = def.innerType as AnyZod
      continue
    }

    if (DEFAULT_WRAPPERS.has(type)) {
      optional ||= !sealed

      if (defaultValue === undefined) {
        defaultValue = def.defaultValue
      }

      current = def.innerType as AnyZod
      continue
    }

    if (type === 'nonoptional') {
      sealed = true
      current = def.innerType as AnyZod
      continue
    }

    /**
     * A union resolves to its FIRST branch, and the branch list is carried on the result.
     *
     * ⚠️ Declaration order is therefore published contract: `z.union([A, B])` and `z.union([B, A])` validate
     * identically and publish differently, and nothing in the source says so. It is documented in
     * service-code-format/02-zod-types.md; do not reorder a union's branches for readability.
     *
     * Only the OUTERMOST union is recorded — a nested one is resolved again when its own branch is walked.
     */
    if (type === 'union') {
      const options = def.options as AnyZod[] | undefined

      // A union with nothing in it resolves to nothing; leave it as the core so the guard reports it.
      if (!options?.length) {
        break
      }

      branches ??= options
      current = options[0]
      continue
    }

    if (TRANSPARENT_WRAPPERS.has(type)) {
      const inner = innerOf(def, type)

      // A wrapper we cannot see inside is the core as far as anything here is concerned; the guard reports it.
      if (!inner) {
        break
      }

      current = inner
      continue
    }

    break
  }

  // metas are collected outer→inner; reverse so outer is applied last (wins) in the merge.
  const meta = Object.assign({}, ...metas.reverse()) as FrMeta
  const core = current as AnyZod
  const resolvedType = core?._zod?.def?.type as string | undefined

  return {
    core,
    coreType: resolvedType || 'string',
    resolvedType,
    required: !optional,
    defaultValue,
    meta,
    ...(branches ? { branches } : {}),
  }
}

export interface WidgetInfo {
  paramType: string
  uiComponent?: Record<string, unknown>
  items?: { type: string }
}

/**
 * The runtime type of a `z.literal(…)`, when every declared value agrees on one — `undefined` for a mixed
 * literal, which has neither a single widget nor a single conversion.
 *
 * Read by BOTH ends: `deriveWidget` publishes the type, ../schema/coerce.ts converts to it. They have to be
 * the same answer — a toggle the console draws for a field the runtime will not accept `'true'` for is the
 * defect this module exists to prevent — which is why the two read one function rather than each deciding.
 * `undefined` is what keeps a mixed literal out of coercion: stringifying the `1` in `z.literal(['a', 1])`
 * would turn a value the declaration accepts into one it rejects.
 */
// `typeof` answers 'object' for null and 'function' for a function — neither is a widget, and `z.literal(null)`
// drew an empty nested form before this was excluded. Only the scalar kinds name a real type.
const LITERAL_KINDS = new Set(['string', 'number', 'boolean', 'bigint'])

export function literalType(core: AnyZod): string | undefined {
  const values = (core._zod?.def?.values as unknown[] | undefined) ?? []
  const kinds = new Set(values.map(value => typeof value))
  const [kind] = [...kinds]

  return kinds.size === 1 && LITERAL_KINDS.has(kind) ? kind : undefined
}

// Derive the v1 param `type` + `uiComponent` from a core zod type (widget is derived from the type — there
// is no `.uiComponent()` in v2). Tokens match the v1 conventions (MULTI_LINE_TEXT/NUMERIC_STEPPER/TOGGLE/…).
//
// TOTAL by design: this is a projection, not a policy. It has to answer for the runtime's own SYSTEM schemes
// too (see ../../model/params-model.ts `toOpaqueBodyParams`), which carry `z.unknown()` and loose records no
// author would be allowed to declare in a parameter. What an AUTHOR may write is decided once, at
// registration, by ./assert-supported-schema.ts.
export function deriveWidget(ins: Inspected): WidgetInfo {
  const { coreType, core, meta } = ins

  // `z.json()` — arbitrary JSON, typed into a textarea. Checked BEFORE the type switch because the
  // declaration underneath is `z.unknown()`: it has to accept the PARSED value once ./coerce.ts has run, so
  // the type says nothing about what the caller supplies. The meta does, and what the caller supplies is text.
  if (meta.frJson) {
    return { paramType: 'STRING', uiComponent: { type: FlowExtensionParamWidgetTypes.MULTI_LINE_TEXT } }
  }

  switch (coreType) {
    case 'string':
      if (meta.frWidget === FlowExtensionParamWidgetTypes.MULTI_LINE_TEXT) {
        return { paramType: 'STRING', uiComponent: { type: FlowExtensionParamWidgetTypes.MULTI_LINE_TEXT } }
      }

      return { paramType: 'STRING' }

    case 'number':
    case 'bigint':
      return { paramType: 'NUMBER', uiComponent: { type: FlowExtensionParamWidgetTypes.NUMERIC_STEPPER } }

    case 'boolean':
      return { paramType: 'BOOLEAN', uiComponent: { type: FlowExtensionParamWidgetTypes.TOGGLE } }

    case 'date':
      return { paramType: 'STRING', uiComponent: { type: FlowExtensionParamWidgetTypes.DATE_PICKER } }

    case 'enum': {
      // `.map({ Label: value })` shows labels; otherwise the enum options are the values.
      // The options are always the declared VALUES — that is what a flow element stores. `.labels()` adds a
      // display name per value, published as `{ value, label }` pairs the console renders directly.
      const declared = ((core.options ?? Object.values((core._zod?.def?.entries as Record<string, unknown>) ?? {})) as unknown[])
        .map(String)
      const labels = meta.frLabels
      const values: unknown[] = labels
        ? declared.map(value => ({ value, label: labels[value] ?? value }))
        : declared

      return { paramType: 'STRING', uiComponent: { type: 'DROPDOWN', options: { values } } }
    }

    // A literal is a one-value type, so it renders as whatever that value IS rather than always as text.
    // A mixed one has no such type and falls back to text.
    case 'literal':
      return deriveWidget({ ...ins, coreType: literalType(core) ?? 'string' })

    // A platform param GROUP names itself: the runtime's own file-options objects (../files-options.ts) carry
    // a token so a consumer can render the group as the thing it is instead of as an anonymous nested form.
    // An author's own `z.object` carries no marker and publishes exactly as it always has.
    case 'object':
      return meta.frWidget
        ? { paramType: 'OBJECT', uiComponent: { type: meta.frWidget } }
        : { paramType: 'OBJECT' }

    // A record is an OBJECT whose KEYS are open. The coercion layer has always walked it as one (see
    // ./coerce.ts); publishing it as one is the model catching up with the runtime.
    case 'record':
      return { paramType: 'OBJECT' }

    case 'array': {
      const element = core._zod?.def?.element as AnyZod | undefined
      const elementType = element ? deriveWidget(inspect(element)).paramType : 'STRING'

      return { paramType: 'LIST', items: { type: elementType } }
    }

    // `z.any()` / `z.unknown()` declare nothing to render or to convert — the pipeline passes them through
    // untouched, and the model has no better word for "anything" than the permissive one.
    case 'any':
    case 'unknown':
      return { paramType: 'STRING' }

    // Reached only by a runtime-owned scheme; an author's is refused at registration. Text is the permissive
    // answer for a SYSTEM param the console never draws.
    default:
      return { paramType: 'STRING' }
  }
}

// The declared fields of a z.object, in declaration order. Tolerates a missing/!object schema by returning
// nothing — an undeclared slot is treated as "no fields" (see ../params.ts).
export function shapeEntries(schema: AnyZod | undefined): [name: string, schema: AnyZod][] {
  const shape = schema?.shape

  return shape && typeof shape === 'object' ? Object.entries(shape) : []
}

// The display labels a field declared with `.labels({ value: 'Label' })`, if any. Display only — nothing is
// translated at runtime, because the value the element stored is already the value the handler wants.
export function fieldLabels(schema: AnyZod): Record<string, string> | undefined {
  return inspect(schema).meta.frLabels
}
